// Tool-call card: compact pill showing the action name, a one-line args
// summary, and a status indicator (running spinner / complete check /
// error / denied). Screenshot rows expand to a thumbnail; all other rows
// are static — the summary already tells the user what happened, and the
// raw JSON dump was noise the user couldn't act on.

import React, { useEffect, useState } from "react";
import {
  Loader2,
  CheckCircle2,
  XCircle,
  ShieldAlert,
  Ban,
  ChevronRight,
  X,
} from "lucide-react";
import { typedTextLabel } from "../typedTextLabel";

const summarize = (toolName, args) => {
  switch (toolName) {
    case "left_click":
    case "right_click":
    case "double_click":
      return args.coordinate ? `(${args.coordinate[0]}, ${args.coordinate[1]})` : "";
    case "type":
    case "type_at":
      return args.text ? typedTextLabel(args.text) : "";
    case "key":
      return args.text ? args.text : "";
    case "scroll":
      return args.coordinate ? `at (${args.coordinate[0]}, ${args.coordinate[1]})` : "";
    case "screenshot":
      return "";
    default:
      return "";
  }
};

function stateBadge(status, result) {
  // `legacy` is a hook class that other code (and tests) discriminate on by
  // state name. It carries no visual styling itself — the Tailwind classes
  // on `ring` and `tint` do that.
  if (status?.type === "requires-action") {
    return {
      icon: <ShieldAlert className="tw-h-3.5 tw-w-3.5" />,
      ring: "tw-border-accent",
      tint: "tw-text-accent",
      label: "Awaiting decision",
      legacy: "copilot-action--pending",
    };
  }
  if (result?.user_decision === "denied") {
    return {
      icon: <Ban className="tw-h-3.5 tw-w-3.5" />,
      ring: "tw-border-border tw-opacity-70",
      tint: "tw-text-muted-foreground",
      label: "Denied",
      legacy: "copilot-action--denied",
    };
  }
  if (result?.ok === false) {
    return {
      icon: <XCircle className="tw-h-3.5 tw-w-3.5" />,
      ring: "tw-border-destructive/60",
      tint: "tw-text-destructive",
      label: "Failed",
      legacy: "copilot-action--error",
    };
  }
  if (status?.type === "complete") {
    return {
      icon: <CheckCircle2 className="tw-h-3.5 tw-w-3.5" />,
      ring: "tw-border-emerald-700/50",
      tint: "tw-text-emerald-400",
      label: "Done",
      legacy: "copilot-action--complete",
    };
  }
  return {
    icon: <Loader2 className="tw-h-3.5 tw-w-3.5 tw-animate-spin" />,
    ring: "tw-border-border",
    tint: "tw-text-muted-foreground",
    label: "Running",
    legacy: "copilot-action--running",
  };
}

// Thumbnail of a screenshot result; click opens a full-size lightbox so the
// user can inspect what the model saw. Mirrors the ScreenshotPart approach
// used by assistant messages with bubble-level image content.
function ScreenshotResultImage({ src }) {
  const [zoomed, setZoomed] = useState(false);
  useEffect(() => {
    if (!zoomed) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setZoomed(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoomed]);
  return (
    <>
      <button
        type="button"
        className="copilot-msg-shot"
        onClick={() => setZoomed(true)}
        aria-label="Open screenshot full size"
      >
        <img src={src} alt="Captured screenshot" />
      </button>
      {zoomed && (
        <div
          className="copilot-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Screenshot"
          onClick={() => setZoomed(false)}
        >
          <img src={src} alt="Captured screenshot" onClick={(e) => e.stopPropagation()} />
          <button
            type="button"
            className="copilot-lightbox__close"
            onClick={() => setZoomed(false)}
            aria-label="Close"
          >
            <X className="tw-h-4 tw-w-4" />
          </button>
        </div>
      )}
    </>
  );
}

export function ActionCard({ toolName, args, status, result }) {
  const [open, setOpen] = useState(false);
  const badge = stateBadge(status, result);
  const screenshotSrc =
    toolName === "screenshot" && result?.image_data_url
      ? result.image_data_url
      : null;
  const expandable = screenshotSrc != null;

  const rowInner = (
    <>
      {expandable ? (
        <ChevronRight
          className={`tw-h-3 tw-w-3 tw-shrink-0 tw-text-muted-foreground tw-transition-transform ${open ? "tw-rotate-90" : ""}`}
        />
      ) : (
        <span className="tw-w-3 tw-shrink-0" aria-hidden="true" />
      )}
      <span className={`tw-flex tw-h-4 tw-w-4 tw-shrink-0 tw-items-center tw-justify-center ${badge.tint}`}>
        {badge.icon}
      </span>
      <span className="tw-font-mono tw-font-semibold tw-uppercase tw-tracking-wider tw-text-[11px] tw-text-copilot-primary">
        {toolName}
      </span>
      <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-muted-foreground">
        {summarize(toolName, args)}
      </span>
    </>
  );

  return (
    <div
      className={`copilot-action ${badge.legacy} tw-my-1 tw-rounded-lg tw-border tw-bg-muted tw-text-xs ${badge.ring}`}
    >
      {expandable ? (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={`${toolName} — ${badge.label}`}
          className="tw-flex tw-w-full tw-items-center tw-gap-2 tw-px-2 tw-py-1.5 tw-text-left"
        >
          {rowInner}
        </button>
      ) : (
        <div
          className="tw-flex tw-w-full tw-items-center tw-gap-2 tw-px-2 tw-py-1.5"
          aria-label={`${toolName} — ${badge.label}`}
        >
          {rowInner}
        </div>
      )}
      {expandable && open && (
        <div className="tw-mx-2 tw-mb-2 tw-flex tw-flex-col tw-gap-2">
          <ScreenshotResultImage src={screenshotSrc} />
        </div>
      )}
    </div>
  );
}
